import React, { createRef, useRef } from 'react';
import clsx from 'clsx';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/SearchRounded';
import ClearIcon from '@mui/icons-material/ClearRounded';
import HelpIcon from '@mui/icons-material/HelpOutlineRounded';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';

import { Flex } from 'src/components/flex';
import Responsive from 'src/components/responsive/Responsive';

import styles from 'src/components/search/Search.module.css';

export type SearchProps = Omit<TextFieldProps<'outlined'>, 'variant' | 'value' | 'onChange'> & {
    value: string;
    onChange: (value: string) => void;
    isLoading?: boolean;
    hotkey?: string; // If present, it will be displayed on the right.
    minSearchLength?: number;
}

/**
 * @param helperText If present, it will be displayed as a tooltip on the help icon.
 * @param hotkey If present, it will be displayed on the right. Mutually exclusive with `helperText`.
 * @param minSearchLength If present, a dropdown hint is shown when the user has typed fewer characters than this value.
 */
export default function Search({
    className,
    value,
    onChange,
    helperText,
    isLoading,
    hotkey,
    minSearchLength,
    ...props
}: SearchProps) {
    if (helperText && hotkey) {
        throw new Error('Cannot use both `helperText` and `hotkey` props in Search component.');
    }

    const [isFocused, setIsFocused] = React.useState(false);
    const ref = createRef<HTMLInputElement>();
    const containerRef = useRef<HTMLDivElement>(null);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
    };

    const handleClear = () => {
        onChange('');
    };

    const onHotkeyClick = () => {
        ref.current?.focus();
    };

    const showMinLengthHint = !!minSearchLength && isFocused && value.length > 0 && value.length < minSearchLength;

    return (
        <div ref={containerRef} className={styles.container}>
            <TextField
                {...props}
                ref={ref}
                type="search"
                className={clsx(styles.root, className)}
                autoComplete="off"
                size="small"
                fullWidth
                value={value}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onChange={handleChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <Flex alignItems="center">
                                {isLoading && <CircularProgress size={20} />}
                                {!isLoading && !!value && (
                                    <IconButton sx={{ padding: 0 }} onClick={handleClear}>
                                        <ClearIcon />
                                    </IconButton>
                                )}
                                {(!!hotkey && !isFocused && !value) && (
                                    <Responsive desktop>
                                        <span role="button" className="searchHotkey" onClick={onHotkeyClick}>{hotkey}</span>
                                    </Responsive>
                                )}
                                {!!helperText && (
                                    <Tooltip title={helperText}>
                                        <HelpIcon />
                                    </Tooltip>
                                )}
                            </Flex>
                        </InputAdornment>
                    ),
                }}
            />
            <Popper open={showMinLengthHint} anchorEl={containerRef.current} placement="bottom-start" style={{ zIndex: 1300, width: containerRef.current?.offsetWidth }}>
                <Paper elevation={2} sx={{ px: 1.5, py: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Type at least {minSearchLength} characters to search
                    </Typography>
                </Paper>
            </Popper>
        </div>
    );
}
